import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { Combobox } from "@/components/ui/combobox";
import { formatDateWithoutTimezoneToFormat } from "@/lib/api/date";
import { parseLayerDate } from "@/section/otc/contract-form-v3/snapshotToForm";
import {
  CommodityOptions,
  generateAccountLabel,
  TradingAccount,
} from "@/lib/api/otc";
import { OptionMonthOptions, OlderOptionMonthOptions } from "@/lib/api/common";
import { Trader } from "@/lib/api/reports";
import {
  CreateOtcErrorCorrectionInput,
  OtcErrorCorrectionDto,
} from "@/lib/api/otcErrorCorrection";

/** Field-level validation errors keyed by input name. */
export type OtcErrorCorrectionFormErrors = Partial<
  Record<keyof CreateOtcErrorCorrectionInput, string>
>;

/** Option-month choices: older + current/future, de-duplicated by value. */
export const ErrorCorrectionOptionMonthOptions: { value: string; label: string }[] =
  (() => {
    const seen = new Set<string>();
    const merged: { value: string; label: string }[] = [];
    for (const option of [...OlderOptionMonthOptions, ...OptionMonthOptions]) {
      if (seen.has(option.value)) continue;
      seen.add(option.value);
      merged.push(option);
    }
    return merged;
  })();

/**
 * Empty form values for a fresh create. `date` is defaulted to today by the
 * modal (computed the same way it is serialized) — the empty default here is
 * blank so callers control the default explicitly. The two contract/layer
 * context links default to null and are only set when creating from a
 * contract's layer.
 */
export function emptyErrorCorrectionValues(): CreateOtcErrorCorrectionInput {
  return {
    trading_contract_id: null,
    trading_contract_layer_id: null,
    trading_account_id: "",
    commodity: "",
    quantity: "",
    option_premium: "",
    option_month: "",
    date: "",
    trader_id: "",
    notes: null,
  };
}

/** Seeds form values from an existing error-correction row. */
export function dtoToFormValues(
  dto: OtcErrorCorrectionDto,
): CreateOtcErrorCorrectionInput {
  return {
    trading_contract_id: dto.trading_contract_id,
    trading_contract_layer_id: dto.trading_contract_layer_id,
    trading_account_id: dto.trading_account_id ?? "",
    commodity: dto.commodity,
    quantity: dto.quantity,
    option_premium: dto.option_premium,
    option_month: dto.option_month,
    date: apiDateToFormDate(dto.date),
    trader_id: dto.trader_id ?? "",
    notes: dto.notes,
  };
}

/**
 * Parses a portal-date/ISO string (interpreted in CommonDestinationTimezone)
 * into a `YYYY-MM-DD` form string, taking the local calendar day so the
 * displayed day matches the stored day regardless of the browser timezone.
 */
function apiDateToFormDate(value: string | null): string {
  if (!value) return "";
  const parsed = parseLayerDate(value);
  return parsed ? formatDateWithoutTimezoneToFormat(parsed, "YYYY-MM-DD") : "";
}

/** API `YYYY-MM-DD` string → local-midnight Date for the picker. */
function formDateToPicker(value: string): Date | undefined {
  if (!value) return undefined;
  return parseLayerDate(value);
}

/**
 * Picker Date (whose local Y/M/D is what the user chose) → `YYYY-MM-DD` string,
 * taking the local calendar day directly (never through an absolute instant).
 */
function pickerToFormDate(date: Date | undefined): string {
  if (!date) return "";
  return formatDateWithoutTimezoneToFormat(date, "YYYY-MM-DD");
}

interface OtcErrorCorrectionFormProps {
  values: CreateOtcErrorCorrectionInput;
  onChange: (values: CreateOtcErrorCorrectionInput) => void;
  traders: Trader[];
  accounts: TradingAccount[];
  errors?: OtcErrorCorrectionFormErrors;
  disabled?: boolean;
}

/**
 * Controlled error-correction form. Holds no internal state; the parent owns
 * `values` and receives every change via `onChange`. Reused by the create and
 * update modals. The two contract/layer context links are not surfaced here.
 */
export function OtcErrorCorrectionForm({
  values,
  onChange,
  traders,
  accounts,
  errors,
  disabled,
}: OtcErrorCorrectionFormProps) {
  const setField = <K extends keyof CreateOtcErrorCorrectionInput>(
    key: K,
    value: CreateOtcErrorCorrectionInput[K],
  ) => {
    onChange({ ...values, [key]: value });
  };

  const traderOptions = traders.map((trader: Trader) => ({
    value: trader.id,
    label: trader.name || "",
  }));
  const accountOptions = accounts
    .map((account: TradingAccount) => ({
      value: account.id,
      label: account.account_id || "",
    }));

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Trading Account</Label>
          <Combobox
            value={values.trading_account_id}
            options={accountOptions}
            onValueChange={(value: string) => setField("trading_account_id", value)}
            placeholder="Select a trading account..."
            popoverClassName="min-w-[400px]"
            disabled={disabled}
            isModal
            dwimOnChange
            virtualizeList
          />
          {errors?.trading_account_id && (
            <span className="text-xs text-destructive">{errors.trading_account_id}</span>
          )}
        </div>

        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Commodity</Label>
          <Combobox
            value={values.commodity}
            options={CommodityOptions}
            onValueChange={(value: string) => setField("commodity", value)}
            placeholder="Select a commodity..."
            popoverClassName="min-w-[400px]"
            disabled={disabled}
            isModal
            dwimOnChange
            virtualizeList
          />
          {errors?.commodity && (
            <span className="text-xs text-destructive">{errors.commodity}</span>
          )}
        </div>

        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Trader</Label>
          <Combobox
            value={values.trader_id}
            options={traderOptions}
            onValueChange={(value: string) => setField("trader_id", value)}
            placeholder="Select a trader..."
            popoverClassName="min-w-[400px]"
            disabled={disabled}
            isModal
            dwimOnChange
            virtualizeList
          />
          {errors?.trader_id && (
            <span className="text-xs text-destructive">{errors.trader_id}</span>
          )}
        </div>

        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Quantity</Label>
          <Input
            type="text"
            disabled={disabled}
            value={values.quantity}
            onChange={(e) => setField("quantity", e.target.value)}
          />
          {errors?.quantity && (
            <span className="text-xs text-destructive">{errors.quantity}</span>
          )}
        </div>

        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Option Premium</Label>
          <Input
            type="text"
            disabled={disabled}
            value={values.option_premium}
            onChange={(e) => setField("option_premium", e.target.value)}
          />
          {errors?.option_premium && (
            <span className="text-xs text-destructive">
              {errors.option_premium}
            </span>
          )}
        </div>

        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Option Month</Label>
          <Combobox
            value={values.option_month}
            options={ErrorCorrectionOptionMonthOptions}
            onValueChange={(value: string) => setField("option_month", value)}
            placeholder="Select an option month..."
            popoverClassName="min-w-[400px]"
            disabled={disabled}
            isModal
            dwimOnChange
            virtualizeList
          />
          {errors?.option_month && (
            <span className="text-xs text-destructive">
              {errors.option_month}
            </span>
          )}
        </div>

        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Date</Label>
          <DatePicker
            value={formDateToPicker(values.date)}
            onChange={(date: Date | undefined) =>
              setField("date", pickerToFormDate(date))
            }
            disabled={disabled}
          />
          {errors?.date && (
            <span className="text-xs text-destructive">{errors.date}</span>
          )}
        </div>
      </div>

      <div className="grid items-center gap-1.5">
        <Label className="font-semibold">Notes</Label>
        <Input
          type="text"
          disabled={disabled}
          value={values.notes ?? ""}
          onChange={(e) => setField("notes", e.target.value || null)}
        />
        {errors?.notes && (
          <span className="text-xs text-destructive">{errors.notes}</span>
        )}
      </div>
    </div>
  );
}
